/*!
 * KoliBri - The accessible HTML-Standard
 */
import { a as __rest } from './tslib.es6-QNbPBOk5.js';
import { h, F as Fragment } from './index-C_ZKddLG.js';
import { B as BaseWebComponent } from './base-web-component-D909Fl-Y.js';
import { T as TooltipBehavior } from './behavior-Co00uT-D.js';
import { T as TooltipFC } from './component-BXCnkVUN.js';
import './common-D-vggmh2.js';
import { b as buildBadgeTextString, s as showExpertSlot } from './component-DepnuZGT.js';
import { K as KolFormFieldLabelFc, b as KolFormFieldHintFc, g as getMsgType, i as isMsgDefinedAndInputTouched } from './FormFieldStateWrapper-CANKwnKv.js';
import { c as clsx } from './clsx-COFh-Vc8.js';
import { a as createRelatedUniqueId } from './dev.utils-Cib2ENyx.js';

const fieldControlTooltipBehaviorPool = new Map();
const getFieldControlTooltipBehavior = (id) => {
    const tooltipBehavior = fieldControlTooltipBehaviorPool.get(id);
    if (tooltipBehavior) {
        return tooltipBehavior;
    }
    const nextTooltipBehavior = new TooltipBehavior(BaseWebComponent.stateLess);
    nextTooltipBehavior.componentWillLoad({ label: '' });
    fieldControlTooltipBehaviorPool.set(id, nextTooltipBehavior);
    return nextTooltipBehavior;
};
const destroyFieldControlTooltipBehavior = (id) => {
    const tooltipBehavior = fieldControlTooltipBehaviorPool.get(id);
    if (tooltipBehavior) {
        tooltipBehavior.destroy();
        fieldControlTooltipBehaviorPool.delete(id);
    }
};
const InputContainer = (_a, children) => {
    var { class: classNames } = _a, other = __rest(_a, ["class"]);
    return (h("div", Object.assign({ class: clsx('kol-field-control__input', classNames) }, other), children));
};
const KolFieldControlFc = (props, children) => {
    const { class: classNames, id, disabled, label, hideLabel, labelAlign, renderNoTooltip, hint, infoPopover, renderNoHint, accessKey, shortKey, msg, touched, required, readonly, tooltipAlign, tooltipFloatingRef, fieldControlInputProps, fieldControlLabelProps, fieldControlTooltipProps, fieldControlHintProps } = props, other = __rest(props, ["class", "id", "disabled", "label", "hideLabel", "labelAlign", "renderNoTooltip", "hint", "infoPopover", "renderNoHint", "accessKey", "shortKey", "msg", "touched", "required", "readonly", "tooltipAlign", "tooltipFloatingRef", "fieldControlInputProps", "fieldControlLabelProps", "fieldControlTooltipProps", "fieldControlHintProps"]);
    const canShowHint = !renderNoHint;
    const canShowTooltip = !renderNoTooltip;
    const hasExpertSlot = showExpertSlot(label);
    const useTooltipInsteadOfLabel = canShowTooltip && !hasExpertSlot && hideLabel;
    const badgeText = buildBadgeTextString(accessKey, shortKey);
    const labelId = createRelatedUniqueId(id, 'label');
    const tooltipBehavior = useTooltipInsteadOfLabel ? getFieldControlTooltipBehavior(id) : undefined;
    if (tooltipBehavior) {
        tooltipBehavior.watchAlign(tooltipAlign);
        tooltipBehavior.watchBadgeText(badgeText || '');
        tooltipBehavior.watchId(labelId);
        tooltipBehavior.watchLabel(label);
    }
    else {
        destroyFieldControlTooltipBehavior(id);
    }
    const forwardedInputRef = fieldControlInputProps === null || fieldControlInputProps === void 0 ? void 0 : fieldControlInputProps.ref;
    const setInputContainerRef = (el) => {
        forwardedInputRef === null || forwardedInputRef === void 0 ? void 0 : forwardedInputRef(el);
        if (tooltipBehavior && el) {
            tooltipBehavior.initContext(el);
            tooltipBehavior.syncListeners(undefined, el, true);
        }
    };
    const components = [
        h(Fragment, null, h(InputContainer, Object.assign({}, fieldControlInputProps, { ref: setInputContainerRef }), children), useTooltipInsteadOfLabel && (h("div", { class: clsx('kol-form-field__tooltip', fieldControlTooltipProps === null || fieldControlTooltipProps === void 0 ? void 0 : fieldControlTooltipProps.class) }, h(TooltipFC, { badgeText: badgeText || '', label: label, align: tooltipAlign, id: labelId, refFloating: tooltipFloatingRef !== null && tooltipFloatingRef !== void 0 ? tooltipFloatingRef : ((el) => {
                tooltipBehavior === null || tooltipBehavior === void 0 ? void 0 : tooltipBehavior.setTooltipElementRef(el);
            }) })))),
        h(KolFormFieldLabelFc, Object.assign({}, (fieldControlLabelProps || {}), { id: id, baseClassName: "kol-field-control", class: clsx(fieldControlLabelProps === null || fieldControlLabelProps === void 0 ? void 0 : fieldControlLabelProps.class, {
                ['kol-field-control__label--visually-hidden']: Boolean(hideLabel),
            }), hasExpertSlot: hasExpertSlot, label: label, accessKey: accessKey, shortKey: shortKey, infoPopover: infoPopover })),
    ];
    if (labelAlign === 'left') {
        components.reverse();
    }
    const stateCssClasses = {
        ['kol-field-control--disabled']: Boolean(disabled),
        ['kol-field-control--required']: Boolean(required),
        ['kol-field-control--touched']: Boolean(touched),
        ['kol-field-control--hide-label']: Boolean(hideLabel),
        ['kol-field-control--read-only']: Boolean(readonly),
        [`kol-field-control--${getMsgType(msg)}`]: Boolean(isMsgDefinedAndInputTouched(msg, touched)),
        [`kol-field-control--label-align-${labelAlign}`]: Boolean(labelAlign),
    };
    return (h("div", Object.assign({ class: clsx('kol-field-control', stateCssClasses, classNames) }, other), components, canShowHint && h(KolFormFieldHintFc, Object.assign({}, (fieldControlHintProps || {}), { baseClassName: "kol-field-control", id: id, hint: hint }))));
};

function getFieldControlProps(state) {
    const props = {
        id: state._id,
        disabled: state._disabled,
        msg: state._msg,
        hint: state._hint,
        label: state._label,
        hideLabel: state._hideLabel,
        touched: state._touched,
    };
    if ('_required' in state) {
        props.required = state._required;
    }
    if ('_readOnly' in state) {
        props.readonly = state._readOnly;
    }
    if ('_labelAlign' in state) {
        props.labelAlign = state._labelAlign;
    }
    if ('_accessKey' in state) {
        props.accessKey = state._accessKey;
    }
    if ('_shortKey' in state) {
        props.shortKey = state._shortKey;
    }
    return props;
}
const FieldControlStateWrapper = (_a, children) => {
    var { state } = _a, other = __rest(_a, ["state"]);
    return (h(KolFieldControlFc, Object.assign({}, getFieldControlProps(state), other), children));
};

export { FieldControlStateWrapper as F };
//# sourceMappingURL=FieldControlStateWrapper-CL8sIEsf.js.map

//# sourceMappingURL=FieldControlStateWrapper-CL8sIEsf.js.map